// src/encounter/lib/to-patient-assessment.ts
// A/P SAVE MAPPER (PROD-1297; v2 model PROD-1422). Builds the authoritative PatientAssessment wire
// body from the DS AssessmentPlanCanvas v2 state — a flat `diagnoses: DxCard[]` ({id, code?, display,
// isPrimary?}) PLUS a top-level `orders: PlanOrder[]` (each linking 1..N diagnoses via `dxIds`) —
// plus the two app-side side-maps that carry what the canvas drops:
//   • OrderPayloadMap (orderId → structured order payload)  — order codes/dosage/sub-tests/system
//   • DxCodingMap     (dxId    → preserved Dx coding)        — Dx code/display/system
//
// The canvas can NOT round-trip structured order fields (PlanOrder is {id,type,label} only), so the
// real coding lives in the side-maps — see order-payload.ts. This mapper widens those minimal codings
// into the full wire Code / CodeDisplayName / ProcedureCode objects and assembles the per-Dx tree.
//
// Wire rules (authoritative — from the PROD-1297 wire contract, NOT re-derived from Flutter):
//  • Each DxCard → an AssessmentPlan in assessmentPlanCondition[]; conditionCode from DxCodingMap;
//    isPrimary = the card's `isPrimary` flag (NOT index===0 — v2 PROD-1422); encounter/subject/
//    performer stamped; childMedicalCode always present.
//  • MULTI-Dx = FAN-OUT (corrected 2026-07-30, PROD-1801). An order linked to N diagnoses is written
//    ONCE UNDER EACH of them. The earlier note here claimed "every other order type is single-link by
//    BE contract" — that was WRONG and cost real links: grouping by dxIds[0] silently dropped every
//    link past the first. Proven by a captured PUT off the live Flutter app (dev-provider, 2026-07-30):
//    one lab against two diagnoses produced two nested radiologyOrderRequest entries, one per Dx,
//    each with parentId:null. The NESTING is authoritative; parentId is a redundant back-reference.
//    Medications fan out too (PROD-1428, Flutter parity): new_ap_bloc.dart updateItemDiagnosis puts the
//    same med under every selected Dx, and each copy's toJson carries the FULL rxDiagnosisCodesRequest
//    (patient_assessment_model.dart:1296). The ONE exception, emitted once under dxIds[0], is referral
//    (top-level, never nested). Orders bucket by type into each linked Dx's childMedicalCode:
//      medication → assessmentPlanMedication[]  (occurenceRequest each w/ zero-GUID medicationId;
//                   rxDiagnosisCodesRequest = one entry per linked Dx, keyed by code, on EVERY copy)
//      lab/imaging → radiologyOrderRequest[]    (categoryCode discriminates lab vs imaging;
//                   labOrderValueSetTestDetails set for lab, null for imaging)
//      procedure  → assessmentPlanProcedures[] (cpt, procedureCode) | assessmentPlanSnomed[] (snomed)
//      referral   → TOP-LEVEL PatientAssessment.referrals[] (NOT nested under the Dx)
//  • Every order carries a flat parentId = parent Dx id (redundant back-ref; nesting is authoritative).
//  • identifier = ctx.identifier ?? null (null on create). Emit `isOrderSign` (NOT isOrderSent).
// PURE — no I/O.
import type {
  AssessmentCoding,
  AssessmentPlan,
  AssessmentPlanMedication,
  AssessmentPlanVaccination,
  AssessmentRadiology,
  AssessmentReferral,
  ChildMedicalCode,
  CodeDisplayName,
  DxCode,
  OccurenceRequest,
  PatientAssessment,
  OmittedTestCoding,
  ProcedureCode,
  RxDiagnosisCodesRequest,
} from '@/types/assessment-api';
import type { CodedOrderPayload, DxCoding, DxCodingMap, OrderCoding, OrderPayloadMap } from './order-payload';
import { isSyntheticId } from './order-payload';
import { categoryFor } from './order-category';
import { toOrderLabs } from './lab-vendor';
import { buildOccurrenceRequests, MED_FREQUENCY, type MedFrequency } from './medication-frequency';
import { educationToRequest, type EducationArticle } from './to-education-article';
import { plainTextToDeltaJson } from '@/dashboard/quill-delta';
import { noteDeltaHasContent } from './append-to-assessment-note';

/** MANDATORY literal zero-GUID for OccurenceRequest.medicationId. */
export const ZERO_GUID = '00000000-0000-0000-0000-000000000000';

/**
 * FAN-OUT POLICY (PROD-1801) — per order type, does one order nest under EVERY linked Dx, or exactly
 * once under `dxIds[0]`?
 *
 * `Record<OrderType, …>` deliberately: adding a new order type is a COMPILE ERROR until its policy is
 * declared here, so a future type cannot silently inherit fan-out. (Review finding: the previous
 * inline guard was a manual audit item.)
 *
 * VERIFICATION STATUS — not all of these are equally proven, and the difference matters:
 *   • lab + imaging  — CONFIRMED on the wire. Captured PUT (dev-provider.scrips.com, 2026-07-30):
 *     one lab ticked against two diagnoses produced two nested radiologyOrderRequest entries.
 *   • procedure + vaccination — UNVERIFIED. Fan-out here is STRUCTURAL INFERENCE: they ride the same
 *     shared Flutter add-widget (`base_ap_item_add_widget.dart:60` holds selectedDiagnosis as a List
 *     for every type) and the same per-Dx childMedicalCode nesting. No captured PUT of either type
 *     yet. **Vaccination is the higher-stakes one** — a wrong fan-out there means duplicate
 *     ADMINISTRATION records, not a duplicate lab row. Capture a two-Dx PUT for vaccination before
 *     treating this as settled.
 *   • medication — fans out, exactly as Flutter does (PROD-1428): new_ap_bloc.dart:984-1026 inserts the
 *     same med under every selected Dx, and every copy carries the full `rxDiagnosisCodesRequest[]`
 *     (patient_assessment_model.dart:1296). The reload fan-in (from-patient-assessment pushOrder) merges
 *     the copies back into one order linked to all of them, as Flutter's `medications()` does.
 *     Known backend behaviour (same as Flutter, accepted by Andrew 2 Oct 2026): at sign the BE runs
 *     CreateUpdateMedications once per nested copy. A later copy reuses the first copy's prescription
 *     only if it finds an active administration with `EndDate.Date >= UtcNow`
 *     (Scrips.Patient AssessmentPlanMedicationService.cs:411-414). A course that ends the same day (no
 *     duration, a 1-day duration, frequency `now`) misses that lookup, so each extra Dx can create a
 *     duplicate prescription. Flutter-authored encounters already behave this way; no BE change.
 *   • referral — single by design: top-level on PatientAssessment, never nested under a Dx.
 */
const FAN_OUT: Record<OrderType, boolean> = {
  lab: true,          // confirmed on the wire
  imaging: true,      // confirmed on the wire (shares radiologyOrderRequest with lab)
  procedure: true,    // UNVERIFIED — structural inference
  vaccination: true,  // UNVERIFIED — structural inference; highest stakes if wrong
  medication: true,   // Flutter parity (PROD-1428); every copy carries the full rxDiagnosisCodesRequest[]
  referral: false,    // top-level, never nested
};

/**
 * The canvas order-type union. `vaccination` (PROD-1227) rides the canvas like any other order so it
 * round-trips losslessly through save/load (otherwise toPatientAssessment would emit
 * assessmentPlanVaccination:[] and wipe nested vaccinations on every autosave). Its payload carries
 * the full wire entry (order-payload.ts), unlike the other minimal-coding kinds.
 */
export type OrderType = 'lab' | 'medication' | 'imaging' | 'procedure' | 'referral' | 'vaccination';

/**
 * A canvas PlanOrder (v2 PROD-1422 — TOP-LEVEL, not nested): lossy id/type/label + the diagnoses it
 * justifies (`dxIds`, 1..N). Structured order data lives in OrderPayloadMap keyed by `id`.
 */
export interface PlanOrder {
  id: string;
  type: OrderType;
  label: string;
  /** The diagnoses this order justifies (1..N). Persistence uses dxIds[0] as the parent — see TODO. */
  dxIds: string[];
}

/**
 * A canvas Dx card (v2 PROD-1422): lossy — code is a bare string (full coding lives in DxCodingMap);
 * `isPrimary` carries the one-primary flag (orders are no longer nested — they're a top-level list).
 */
export interface DxCard {
  id: string;
  code?: string;
  display: string;
  /** Exactly one Dx is primary (the canvas enforces one-primary). Written to the wire per-Dx. */
  isPrimary?: boolean;
  /**
   * When this diagnosis was recorded. Flutter stamps every added diagnosis with `now`
   * (new_ap_bloc.dart:711) and the wire round-trips it; React sent null, so the onset date was never
   * written AND a Flutter-written one was nulled on the next save — breaking acute-vs-chronic reading
   * and any onset-based reporting. Carried here so the save PRESERVES a loaded value and only a
   * genuinely new Dx is stamped: the same shape the education article's onSetDate needed after it
   * drifted (PROD-1801).
   */
  onSetDate?: string;
  /** The Dx plan's `endDate` as loaded — sent back unchanged, as Flutter does (PROD-2854). */
  endDate?: string;
}

export interface PatientAssessmentContext {
  encounter: string;
  subject: string;
  performer: string;
  /** Assessment id on update; null/absent on create. */
  identifier?: string | null;
  /**
   * The set of Dx (DxCard.id) and order (PlanOrder.id) ids that the SERVER has already assigned —
   * i.e. nodes that came back populated from a prior save/fetch (the page reconciles them into the
   * canvas ids after each save; see from-patient-assessment.ts). For these we echo the id back as
   * the node `identifier` so a PUT UPDATES the row in place. Nodes NOT in this set are client-only
   * (just added, never persisted) → identifier stays null so the BE CREATEs them. Absent/empty set
   * → every node sends null (the create-time default; unchanged behavior). PROD-1297 review MAJOR 2:
   * without this the PUT re-sends null child ids and the BE would DUPLICATE child rows.
   */
  serverIds?: ReadonlySet<string>;
  /*
   * TODO(PROD-1801): re-key by (orderId, dxId) before any surface enables RE-SAVE of a multi-Dx
   * order. This set is keyed by a single order id, which is sound today only because the fan-out
   * sends `identifier: null` for every nested copy, so the BE creates them and no copy needs its
   * own id echoed back. The moment a surface has to UPDATE a specific nesting in place, one key per
   * order cannot address N rows — the server mints a distinct row id per nesting (captured
   * 2026-07-30: the same lab returned 748c4129… under S32.464K and 2adec1c8… under H31.003).
   * Not fixed here because nothing exercises that path yet, and the honest closer is a live
   * round-trip (save with 2 dx → reload → save again → assert no new rows) which has NOT been run.
   */
  /**
   * Whether orders have been SENT (Flutter `SendOrderEvent` sets `isOrderSent:true, hasOrderSent:true`
   * on the saved apData). Emitted as BOTH `isOrderSign` (KEY-RENAME) and `hasOrderSent`. Default false
   * (create-time / not-yet-sent). CRITICAL: once sent, the page MUST pass the CURRENT sent state on
   * every subsequent autosave, else a later A/P edit would re-emit false and silently UN-send the
   * orders (the flags are top-level on the doc and this mapper is the sole writer). PROD-1410.
   */
  orderSent?: boolean;
  /**
   * Whether the ePrescription is STALE relative to the plan (Flutter `ePrescriptionChanged`, set from
   * changeInMedication at new_ap_bloc.dart:180 and forced true on a med removal at :838). Wrap-Up reads
   * it as `isRxSynced` (wrap_up_billing_screen.dart:263-268).
   *
   * This was hardcoded `false`, which ASSERTS "the prescription is in sync" on every single save — so a
   * medication changed after an eRx submission never flagged the prescription as superseded, and a
   * patient could collect the old one. Passing the loaded value through at minimum stops us overwriting
   * a true flag with false; setting it true on a medication change is the follow-on (A/P-23).
   */
  ePrescriptionChanged?: boolean;
  /**
   * The A/P SECTION narrative (Flutter's commentButton → apData.notes/notePlainText). Emitted as BOTH
   * `notes` and `notePlainText` because we author plain text.
   *
   * This used to be hardcoded `null` here, which did not merely fail to capture the note — it
   * DESTROYED one authored in Flutter on the same encounter, on the first React autosave. Absent
   * ⇒ preserve nothing is not an option; pass the loaded value through so a note you did not edit
   * survives (the caller hydrates it from the GET).
   */
  assessmentNote?: string;
  /**
   * The SAME narrative as a Quill Delta ops-array JSON string — what goes into `notes`.
   *
   * Load-bearing, and it fixes a real cross-client bug: this mapper used to write the PLAIN TEXT
   * into `notes` as well as `notePlainText`. `src/dashboard/quill-delta.ts` records the convention
   * — Flutter reads `notes` through `flutter_quill`'s `tryParseDocument`, so a bare plaintext
   * string there renders BLANK. Every A/P note authored in React was invisible to a doctor opening
   * the same encounter in PA. Absent ⇒ the plain text is encoded as a single-op Delta, so a host
   * that only holds text still writes something Flutter can render.
   */
  assessmentNoteDelta?: string;
  /**
   * Patient-education articles picked on the A&P Education tab (PROD-1454). TOP-LEVEL + Dx-less —
   * emitted into `assessmentPlanArticles` (deduped by display, stamped with encounter/subject/
   * performer/onSetDate). Absent/empty → `[]` (unchanged create-time behavior).
   */
  articles?: EducationArticle[];
}

/**
 * Widen the minimal OrderCoding into the 6-field wire Code.
 *
 * `Definition` IS NOT THE DISPLAY NAME. It is the dictionary's clinical definition, and Flutter uses it
 * for exactly one thing: the TOOLTIP on a search-rail row (ap_item_view_cell.dart:52,112,186,229 →
 * Tooltip(message: info)). It is never a name fallback. We were writing the display name into it, which
 * both destroyed any real dictionary definition on the row AND made the tooltip a redundant echo of the
 * row's own label. Flutter sends null when it has no definition (code.dart:63), so null is the faithful
 * value here (PROD-1801).
 *
 * PRESERVING a server-supplied definition would be better still, but OrderCoding does not carry one —
 * that is a wider change, tracked on A/P-23. This stops us fabricating; it does not yet round-trip.
 */
function toCode(c: OrderCoding | undefined): AssessmentCoding {
  return {
    code: c?.code ?? null,
    Definition: null,
    display: c?.displayName ?? null,
    system: c?.system ?? null,
    description: null,
    shortName: null,
  };
}

/** One omitted panel member on the wire — {code, display, system} (PROD-2632). */
function toOmittedTest(c: OrderCoding): OmittedTestCoding {
  return { code: c.code, display: c.displayName, system: c.system ?? null };
}

/** Widen the minimal OrderCoding into the lab/imaging CodeDisplayName. */
function toCodeDisplayName(c: OrderCoding): CodeDisplayName {
  return {
    code: c.code,
    displayName: c.displayName,
    system: c.system ?? null,
    description: null,
    shortName: null,
  };
}

/** Widen the minimal OrderCoding into the CPT ProcedureCode (capital-S System). */
function toProcedureCode(c: OrderCoding): ProcedureCode {
  return {
    code: c.code,
    display: c.displayName,
    displayName: c.displayName,
    System: c.system ?? null,
  };
}

/** Build the meds' Dx-link row from the parent Dx coding. */
function toDxCode(dx: DxCoding | undefined): DxCode {
  return {
    dxId: null,
    code: dx?.code ?? null,
    displayName: dx?.display ?? null,
    system: dx?.system ?? null,
  };
}

function emptyChildMedicalCode(): ChildMedicalCode {
  return {
    assessmentPlanCondition: [],
    assessmentPlanProcedures: [],
    assessmentPlanSnomed: [],
    assessmentPlanMedication: [],
    assessmentPlanVaccination: [],
    radiologyOrderRequest: [],
  };
}

/**
 * Echo a node's server id back when it's known-persisted; null for client-only (create) nodes.
 * A client-synthesized fallback id (SYNTHETIC_ID_PREFIX) is NEVER server-assigned — even if it
 * leaked into the serverIds set — so a never-persisted order still POSTs (identifier null).
 */
function serverIdOf(ctx: PatientAssessmentContext, id: string): string | null {
  if (isSyntheticId(id)) return null;
  return ctx.serverIds?.has(id) ? id : null;
}

function buildMedication(
  payload: Extract<CodedOrderPayload, { kind: 'medication' }>,
  parentDxId: string,
  orderId: string,
  dxIds: string[],
  dxCodingMap: DxCodingMap,
  ctx: PatientAssessmentContext,
): AssessmentPlanMedication {
  // occurenceRequest[] is built per frequency type (PROD-1340) — every_day → 1 entry, days_interval
  // → +daysInterval, specific_days → 1 per weekday, as_needed/now → []. occurenceTiming stays null
  // (a BE DateTime — never the frequency code, which 400'd in PROD-1337; the code lives in `frequency`).
  const occurenceRequest: OccurenceRequest[] = payload.frequency
    ? buildOccurrenceRequests(
        {
          frequency: payload.frequency as MedFrequency,
          quantity: payload.quantity,
          timesADay: payload.timesADay,
          daysInterval: payload.daysInterval,
          specificDays: payload.specificDays,
        },
        ZERO_GUID,
      )
    : [];
  // v2 PROD-1422 — MULTI-Dx (MEDS ONLY, per the BE contract): one RxDiagnosisCodesRequest per linked
  // diagnosis. AssessmentPlanMedicationService (Scrips.Patient) creates one RxDiagnosisCodes row per
  // entry, keyed by DxCode.Code (DxId stays null on create — same convention as the parentId link,
  // whose GUID isn't known until the Dx is first saved). The med fans out (PROD-1428), so this SAME
  // full list is written on every nested copy, as Flutter's toJson does from localDiagnosis.
  // Dedupe defends a repeated toggle; empty dxIds → parent-only link.
  const linkedDxIds = dxIds.length > 0 ? [...new Set(dxIds)] : [parentDxId];
  const rxDiagnosisCodesRequest: RxDiagnosisCodesRequest[] = linkedDxIds.map((dxId) => ({
    id: null,
    patientRxId: null,
    isActive: true,
    dxCode: toDxCode(dxCodingMap.get(dxId)),
    isDeleted: false,
  }));
  // Frequency `now` carries weight/priority in the JSON-encoded `instruction` (Flutter
  // OtherMedicationInfo). Gate on `now` so a routine/every_day med never emits a stray instruction.
  const instruction =
    payload.frequency === MED_FREQUENCY.now && (payload.weight != null || payload.priority)
      ? JSON.stringify({
          ...(payload.weight != null ? { weight: payload.weight } : {}),
          ...(payload.priority ? { priority: payload.priority } : {}),
        })
      : null;
  return {
    medicationCode: toCode(payload.medicationCode),
    route: payload.route ? toCode(payload.route) : null,
    // BE-required dose fields (PROD-1339): dose unit + dispense + dispense unit.
    // `|| null` (not `??`) so an empty string never reaches the wire as a "present-but-blank"
    // required field — coerce "" → null (e.g. a loaded draft whose BE value was empty).
    unitOfMeasurement: payload.doseUnit || null,
    frequency: payload.frequency ?? null, // coded type (every_day / specific_days / …) — PROD-1340
    duration: payload.duration != null ? String(payload.duration) : null,
    durationUnit: payload.durationUnit ?? null,
    dispense: payload.dispense ?? null,
    dispenseUnit: payload.dispenseUnit || null, // coerce "" → null (see unitOfMeasurement above)
    refill: payload.refill ?? null,
    substitution: payload.substitution ?? null,
    sig: payload.sig ?? null,
    instruction,
    notes: payload.note ?? null,
    notePlainText: payload.note ?? null,
    onSetDate: payload.startDate ?? null, // start date for scheduled frequencies (PROD-1340)
    // The loaded order stamp, sent back unchanged (PROD-2854, Flutter toJson). Only when known: a NEW
    // order sends none and the server stamps it once (`orderDate ?? UtcNow`).
    ...(payload.orderDate ? { orderDate: payload.orderDate } : {}),
    identifier: serverIdOf(ctx, orderId),
    parentId: serverIdOf(ctx, parentDxId), // GUID-or-null (NEVER the raw Dx code — PROD-1340)
    orderId: serverIdOf(ctx, orderId),
    encounter: ctx.encounter,
    assessmentPlanId: null,
    subject: ctx.subject,
    performer: ctx.performer,
    rxDiagnosisCodesRequest,
    occurenceRequest,
    // REQUIRED by the BE (AssessmentPlanMedicationService dereferences it) — omitting the key 500s the
    // whole save (live-probed 2026-07-02: 500→201 on adding `[]`). React runs no drug-interaction
    // check, so always emit an empty list (Flutter emits its interaction results here). PROD-1442.
    // Echo what is already on the row rather than wiping it. Still `[]` when there is nothing —
    // the key is REQUIRED on the wire (omitting it 500s the save, live-probed PROD-1442).
    vidalAlertRequest: payload.vidalAlerts ?? [],
  };
}

function buildRadiology(
  payload: Extract<CodedOrderPayload, { kind: 'lab' } | { kind: 'imaging' }>,
  parentDxId: string,
  orderId: string,
  ctx: PatientAssessmentContext,
): AssessmentRadiology {
  const isLab = payload.kind === 'lab';
  return {
    id: serverIdOf(ctx, orderId),
    code: toCodeDisplayName(payload.code),
    // Flutter's values (108252007/laboratory, 363679005/imaging) — the BE keys lab requests and
    // its server-side reads on them. See order-category.ts (PROD-2619).
    ...categoryFor(isLab ? 'lab' : 'imaging'),
    labOrderValueSetTestDetails: isLab ? payload.subTests.map(toCode) : null,
    // PROD-2632: the deselected members, sent on every save (the BE's IsOmitted rows follow the
    // latest payload). Only when KNOWN — an unknown list (server null / members never loaded) is not
    // sent, which the contract reads as "nothing omitted". Never on imaging.
    ...(isLab && payload.omittedTests ? { omittedTests: payload.omittedTests.map(toOmittedTest) } : {}),
    // PROD-2640 (Patient #1001): members another lab on this plan already orders, with the
    // catalogue code/system verbatim (same shape as omittedTests). Only when the payload has a list.
    ...(isLab && payload.alreadyOrderedTests
      ? { alreadyOrderedTests: payload.alreadyOrderedTests.map(toOmittedTest) }
      : {}),
    // PROD-2640 (Patient #1000): Bill to. Only when set; an absent key keeps what Patient stored.
    ...(isLab && payload.billTo ? { billTo: payload.billTo } : {}),
    // PROD-2849 (Patient PROD-2841): the lab model fields, only on orders from the order-search popup.
    // No cost is ever sent (physicians don't see it); Patient looks it up from labPriceLineId at sign.
    ...(isLab && payload.fulfilmentMode ? { fulfilmentMode: payload.fulfilmentMode } : {}),
    ...(isLab && payload.labPriceLineId ? { labPriceLineId: payload.labPriceLineId } : {}),
    // PROD-2861 (Patient #1028): the order-search item, stored on every save and returned on the read.
    ...(isLab && payload.labItemId ? { labItemId: payload.labItemId } : {}),
    ...(isLab && payload.memberLabs?.length
      ? {
          memberLabs: payload.memberLabs.map((m) => ({
            code: m.code,
            system: m.system ?? null,
            fulfilmentMode: m.fulfilmentMode,
            labVendorId: m.labVendorId ?? null,
            labVendorName: m.labVendorName ?? null,
            labPriceLineId: m.labPriceLineId ?? null,
          })),
        }
      : {}),
    // PROD-2640 P-B6: the order questions' answers (Patient stores them on the order and lab request).
    ...(isLab && payload.aoeAnswers?.length
      ? {
          aoeAnswers: payload.aoeAnswers.map((a) => ({
            questionCode: a.questionCode ?? null,
            questionText: a.questionText,
            answerType: a.answerType ?? null,
            value: a.value,
          })),
        }
      : {}),
    priority: null,
    status: null,
    intent: null,
    note: payload.note ?? null,
    notePlainText: payload.note ?? null,
    parentId: serverIdOf(ctx, parentDxId), // GUID-or-null (never the raw Dx code — PROD-1340)
    assessmentPlanId: null,
    subject: ctx.subject,
    performer: ctx.performer,
    encounter: ctx.encounter,
    isActive: true,
    isDeleted: false,
    // PROD-2653: the performing lab vendor. Labs only; no vendor chosen → null, as before.
    orderLabs: isLab ? toOrderLabs(payload.vendor) : null,
    // PROD-2640: the catalogue v2 panel version, only when the lab was picked from one. A legacy
    // (LabOrderValueSet) pick sends neither key, so its wire is exactly what it was before.
    ...(isLab && payload.panelDefinitionId
      ? { panelDefinitionId: payload.panelDefinitionId, panelVersion: payload.panelVersion ?? null }
      : {}),
  };
}

function buildProcedurePlan(
  payload: Extract<CodedOrderPayload, { kind: 'procedure' }>,
  parentDxId: string,
  orderId: string,
  ctx: PatientAssessmentContext,
): AssessmentPlan {
  const isCpt = payload.system === 'cpt';
  return {
    identifier: serverIdOf(ctx, orderId),
    encounter: ctx.encounter,
    parentId: serverIdOf(ctx, parentDxId), // GUID-or-null (never the raw Dx code — PROD-1340)
    // ADD-TO-BILLING rides `isPrimary` on a procedure plan — Flutter's copyWith routes it there
    // (patient_assessment_model.dart:725), which is surprising but is the contract. We were collecting
    // the clinician's tick (procedure-add-dialog → order-payload.addToBilling) and then hardcoding
    // false here, so the procedure never reached billing AND the edit dialog reopened unticked
    // (PROD-1801).
    isPrimary: payload.addToBilling === true,
    childMedicalCode: emptyChildMedicalCode(),
    conditionCode: null,
    externalCode: null,
    assessmentPlanId: null,
    subject: ctx.subject,
    performer: ctx.performer,
    notes: payload.note ?? null,
    notePlainText: payload.note ?? null,
    // The loaded dates, sent back unchanged (PROD-2854). The server takes the procedure's order date
    // from `endDate` (`EndDate ?? UtcNow`), so null here re-stamped it on every save. New → null.
    onSetDate: payload.onSetDate ?? null,
    endDate: payload.endDate ?? null,
    isDeleted: false,
    procedureCode: isCpt ? toProcedureCode(payload.code) : null,
    snomedCode: isCpt ? null : toCode(payload.code),
    performerDetails: {},
  };
}

function buildReferral(
  payload: Extract<CodedOrderPayload, { kind: 'referral' }>,
  parentDxId: string,
  orderId: string,
  ctx: PatientAssessmentContext,
): AssessmentReferral {
  const additional: RxDiagnosisCodesRequest[] = (payload.additionalDiagnoses ?? []).map((c) => ({
    id: null,
    patientRxId: null,
    isActive: true,
    dxCode: { dxId: null, code: c.code, displayName: c.displayName, system: c.system ?? null },
    isDeleted: false,
  }));
  const wire: AssessmentReferral = {
    id: serverIdOf(ctx, orderId),
    encounter: ctx.encounter,
    assessmentPlanId: null,
    parentId: serverIdOf(ctx, parentDxId), // GUID-or-null (never the raw Dx code — PROD-1340)
    subject: ctx.subject,
    performer: ctx.performer,
    notes: payload.note ?? null,
    notePlainText: payload.note ?? null,
    onSetDate: payload.onSetDate ?? null, // loaded dates round-trip (PROD-2854); new → null
    endDate: payload.endDate ?? null,
    isDeleted: false,
    summary: null,
    diagnosis: [],
    otherDiagnosis: additional,
    // ⚠️ contract-uncertain subtype (ReferralTarget) — see assessment-api.ts. Carry the target name.
    referral: payload.target != null ? { target: payload.target } : null,
  };
  // PROD-1395 / PROD-3336: a speciality (or role) referral (the `referrals` flag) also sends it, the practitioner and the display, with
  // code/display/system null (the role is the target). Without a role the row is exactly as before. status, rootId
  // and the reason fields are the server's and are never sent.
  // PROD-3336: a speciality referral sends the speciality id and name the same way (with the role too for dietetics).
  if (payload.performerRole != null || payload.targetSpecialityId != null) {
    if (payload.performerRole != null) wire.performerRole = payload.performerRole;
    if (payload.targetSpecialityId != null) {
      wire.targetSpecialityId = payload.targetSpecialityId;
      wire.targetSpecialityName = payload.targetSpecialityName ?? null;
    }
    wire.targetPractitionerId = payload.targetPractitionerId ?? null;
    if (payload.targetDisplay != null) wire.targetDisplay = payload.targetDisplay;
    wire.code = null;
    wire.display = null;
    wire.system = null;
    // Agreed with Patient #1159: such a referral sends no legacy target (the server reads it only as a fallback).
    wire.referral = null;
  }
  return wire;
}

/**
 * Re-stamp the carried vaccination entry onto the current parent Dx + encounter context. The payload
 * holds the FULL wire entry (lossless round-trip — order-payload.ts), so we only override the parent
 * link + stamps + identifier; lot/expiry/site/route/dose/orderId/isAdministrationRequested are kept
 * verbatim. `orderId` (the Inventory deduction key) is preserved from the entry, never re-minted.
 */
function buildVaccination(
  payload: Extract<CodedOrderPayload, { kind: 'vaccination' }>,
  parentDxId: string,
  orderId: string,
  ctx: PatientAssessmentContext,
): AssessmentPlanVaccination {
  return {
    ...payload.entry,
    identifier: serverIdOf(ctx, orderId),
    parentId: serverIdOf(ctx, parentDxId), // GUID-or-null (never the raw Dx code — PROD-1340)
    encounter: ctx.encounter,
    subject: ctx.subject,
    performer: ctx.performer,
  };
}

/**
 * Map the canvas state (v2: flat DxCards + a top-level order list + side-maps) into the
 * PatientAssessment wire body.
 * @param dxCards  ordered Dx cards; `isPrimary` (the flag, NOT position) marks the primary Dx
 * @param orders   top-level orders; each nests under its FIRST linked Dx (dxIds[0] = parentId)
 * @param payloads orderId → structured order payload
 * @param dxCoding dxId → preserved Dx coding
 * @param ctx      encounter/subject/performer stamps + optional identifier (null on create)
 *
 * MULTI-Dx (PROD-1422; fan-out PROD-1801, meds PROD-1428): v2 lets an order justify N diagnoses
 * (`dxIds`). Every nested order type is written once under EACH linked Dx (see FAN_OUT); meds also carry
 * the full set as one RxDiagnosisCodesRequest per linked Dx on every copy (see buildMedication).
 * Referrals stay top-level and back-ref only dxIds[0].
 *
 * AUTHORITATIVE BE CONTRACT (Scrips.Patient/Application/Dtos/PatientChart/CreatePatientAssessmentPlanRequest.cs
 * + AssessmentPlanMedicationService.cs:545-559, read 2026-07-01): **only AssessmentPlanMedication carries a
 * multi-Dx array — `List<RxDiagnosisCodesRequest>`; the service creates one RxDiagnosisCodes row per entry,
 * keyed by DxCode.Code.** Every other order type (AssessmentPlanOrder [lab/imaging], AssessmentPlanProcedures,
 * AssessmentPlanSnomed, AssessmentPlanVaccination, AssessmentPlanReferral) has ONLY a single `ParentId` — the
 * BE cannot store multiple Dx links for them, so extending non-med multi-link needs a BE change (new ticket).
 * The meds multi-entry save must be confirmed on a POPULATED encounter via the RUNNING APP (a hand-built curl
 * body has the rx links dropped — medication-save-saga; the BE validates incrementally).
 */
export function toPatientAssessment(
  dxCards: DxCard[],
  orders: PlanOrder[],
  payloads: OrderPayloadMap,
  dxCoding: DxCodingMap,
  ctx: PatientAssessmentContext,
): PatientAssessment {
  const referrals: AssessmentReferral[] = [];

  // FAN-OUT (PROD-1801, 2026-07-30) — an order linked to N diagnoses is written ONCE UNDER EACH.
  // This is how multi-Dx linking is expressed on the wire; it is NOT a single-link contract, and the
  // previous `dxIds[0]`-only grouping silently discarded every link past the first.
  //
  // Proven against the live Flutter app + a captured PUT (dev-provider.scrips.com, 2026-07-30): one
  // lab ticked against two diagnoses produced TWO nested entries —
  //   assessmentPlanCondition[0].childMedicalCode.radiologyOrderRequest = [{id:null, parentId:null, 80050}]
  //   assessmentPlanCondition[1].childMedicalCode.radiologyOrderRequest = [{id:null, parentId:null, 80050}]
  // `parentId` is null on write: the NESTING is authoritative and the server derives the parent.
  // Flutter does the same (new_ap_bloc.dart ~1069-1100 maps over every Dx and pushes the order into
  // each selected one's child list).
  //
  // Orders with no dxIds, or whose parent isn't a current Dx, are dropped (a removed Dx cascades to
  // its orders — matches the host).
  const ordersByDx = new Map<string, PlanOrder[]>();
  for (const order of orders) {
    // Dedupe defensively: a repeated toggle must not nest the same order twice under one Dx.
    for (const dxId of new Set(order.dxIds)) {
      if (dxId == null) continue;
      (ordersByDx.get(dxId) ?? ordersByDx.set(dxId, []).get(dxId)!).push(order);
    }
  }

  const assessmentPlanCondition: AssessmentPlan[] = dxCards.map((card) => {
    const childMedicalCode = emptyChildMedicalCode();
    const coding = dxCoding.get(card.id);

    for (const order of ordersByDx.get(card.id) ?? []) {
      const payload = payloads.get(order.id);
      if (!payload) continue;
      // A single-emit type is skipped in every bucket except its parent, even though the fan-out
      // visits this order under each linked Dx. Policy is declared once in FAN_OUT above and is
      // compiler-enforced exhaustive over OrderType.
      if (!FAN_OUT[payload.kind] && card.id !== order.dxIds[0]) continue;
      switch (payload.kind) {
        case 'medication':
          // Fanned out like every nested type (PROD-1428, Flutter parity): one copy under each linked
          // Dx, each with parentId = that Dx and the FULL rxDiagnosisCodesRequest set. See the
          // known backend behaviour note on FAN_OUT (a same-day course can duplicate at sign).
          childMedicalCode.assessmentPlanMedication.push(
            buildMedication(payload, card.id, order.id, order.dxIds, dxCoding, ctx),
          );
          break;
        case 'lab':
        case 'imaging':
          childMedicalCode.radiologyOrderRequest.push(
            buildRadiology(payload, card.id, order.id, ctx),
          );
          break;
        case 'procedure': {
          const plan = buildProcedurePlan(payload, card.id, order.id, ctx);
          if (payload.system === 'cpt') childMedicalCode.assessmentPlanProcedures.push(plan);
          else childMedicalCode.assessmentPlanSnomed.push(plan);
          break;
        }
        case 'referral':
          // Referrals are TOP-LEVEL on PatientAssessment, NOT nested under the Dx — so the fan-out
          // must not push one copy per linked Dx. Emitted once, back-referencing dxIds[0].
          referrals.push(buildReferral(payload, card.id, order.id, ctx));
          break;
        case 'vaccination':
          // Nested under the Dx (childMedicalCode.assessmentPlanVaccination) — NOT top-level (which
          // stays []). Emitting it here is what stops the autosave from wiping nested vaccinations.
          childMedicalCode.assessmentPlanVaccination.push(
            buildVaccination(payload, card.id, order.id, ctx),
          );
          break;
      }
    }

    const conditionCode: AssessmentCoding | null = coding
      ? {
          code: coding.code ?? null,
          // Not the display name — see toCode above. Flutter uses Definition for the row tooltip only.
          Definition: null,
          display: coding.display ?? null,
          system: coding.system ?? null,
          description: null,
          shortName: null,
        }
      : null;

    return {
      identifier: serverIdOf(ctx, card.id),
      encounter: ctx.encounter,
      parentId: null, // top-level Dx
      isPrimary: card.isPrimary === true, // v2 PROD-1422: from the flag, NOT position (index===0)
      childMedicalCode,
      conditionCode,
      externalCode: null,
      assessmentPlanId: null,
      subject: ctx.subject,
      performer: ctx.performer,
      notes: null,
      notePlainText: null,
      // Preserve a loaded stamp; a NEW Dx is stamped by the host on add (Flutter: new_ap_bloc.dart:711).
      onSetDate: card.onSetDate ?? null,
      endDate: card.endDate ?? null, // a loaded value round-trips (PROD-2854); new → null
      isDeleted: false,
      procedureCode: null,
      snomedCode: null,
      performerDetails: {},
    };
  });

  const narrativeHeld = !!ctx.assessmentNote?.trim() || noteDeltaHasContent(ctx.assessmentNoteDelta);

  return {
    identifier: ctx.identifier ?? null,
    encounter: ctx.encounter,
    subject: ctx.subject,
    performer: ctx.performer,
    // The A/P narrative — a PAIR: `notes` is the Quill Delta the Flutter client renders,
    // `notePlainText` the flattened text. Never the same value in both (see assessmentNoteDelta).
    // A Delta with content and blank text (an image-only note: Quill gives an embed no text) is a
    // note too, so `notes` carries it and the plain half goes out as '' rather than null — the
    // reader falls back from a null `notePlainText` to `notes`, which would show the raw JSON.
    notes: narrativeHeld
      ? (ctx.assessmentNoteDelta?.trim() ?? plainTextToDeltaJson(ctx.assessmentNote ?? ''))
      : null,
    notePlainText: ctx.assessmentNote?.trim() ? ctx.assessmentNote : narrativeHeld ? '' : null,
    isDeleted: false,
    isOrderSign: ctx.orderSent ?? false, // KEY-RENAME — NOT isOrderSent; preserve once sent (PROD-1410)
    hasOrderSent: ctx.orderSent ?? false,
    ePrescriptionChanged: ctx.ePrescriptionChanged ?? false,
    assessmentPlanCondition,
    referrals,
    // Flattened top-level order arrays send [] — real orders live nested per-Dx.
    assessmentPlanArticles: educationToRequest(ctx.articles ?? [], {
      encounter: ctx.encounter,
      subject: ctx.subject,
      performer: ctx.performer,
    }),
    assessmentPlanProcedures: [],
    assessmentPlanSnomed: [],
    assessmentPlanMedication: [],
    assessmentPlanVaccination: [],
    radiologyOrderRequest: [],
  };
}
